import { useEffect, useRef } from 'react'

interface UseInfiniteScrollOptions {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  /**
   * Pre-fetch buffer - how far above the bottom (in CSS pixels) we start
   * loading the next page. 400px ≈ one card-height, so the user sees the
   * next batch already rendered as the previous one scrolls past mid-screen
   * rather than spinning at the very bottom.
   */
  rootMargin?: string
}

/**
 * Wire a sentinel element to trigger `fetchNextPage` when it scrolls into
 * view. Returns the ref to attach to the sentinel <div>.
 *
 *   const sentinelRef = useInfiniteScroll({ hasNextPage, isFetchingNextPage, fetchNextPage });
 *   return <>{rows}<div ref={sentinelRef} /></>;
 *
 * The observer is only active while another page is actually available
 * (`hasNextPage`) and we're not already fetching one - so a fast scroll
 * doesn't fire `fetchNextPage` multiple times in flight.
 */
export function useInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootMargin = '400px',
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          fetchNextPage()
        }
      },
      { rootMargin },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rootMargin])

  return sentinelRef
}
